import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  IconButton,
  MenuCloseIcon,
} from '@noor/ui';
import { cn } from '@noor/lib';

interface PreviewableImageProps {
  src: string;
  alt?: string;
  className?: string;
  loading?: 'eager' | 'lazy';
}

/**
 * Thumbnail image that opens a plain full-size preview on click — no extra
 * layout containers, the thumbnail keeps its exact classes.
 */
export function PreviewableImage({
  src,
  alt = '',
  className,
  loading = 'lazy',
}: PreviewableImageProps) {
  const { t } = useTranslation('moderation');
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  // A dead URL reads as a clear "not loaded" tile, never a broken-image glyph.
  if (failed) {
    return (
      <div
        role="img"
        aria-label={t('detail.imageFailed', 'Изображение не загрузилось')}
        data-testid="image-failed"
        className={cn(
          'flex items-center justify-center bg-muted p-2 text-center text-xs text-muted-foreground',
          className,
        )}
      >
        {t('detail.imageFailed', 'Изображение не загрузилось')}
      </div>
    );
  }
  return (
    <>
      {/* `contents` keeps the button out of layout — the thumbnail renders
          exactly as before, now keyboard-operable. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open image preview"
        className="contents cursor-zoom-in"
      >
        <img
          src={src}
          alt={alt}
          loading={loading}
          onError={() => setFailed(true)}
          className={cn('cursor-zoom-in', className)}
        />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          closeOnInteractOutside
          showClose={false}
          aria-label={alt === '' ? 'Image preview' : alt}
          className="inset-0 top-0 left-0 flex h-screen w-screen max-w-none translate-x-0 translate-y-0 items-center justify-center border-0 bg-transparent p-4 shadow-none sm:max-w-none"
        >
          <IconButton
            variant="ghost"
            aria-label="Close image preview"
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 z-10 size-11 rounded-full bg-black/45 text-white hover:bg-black/65 hover:text-white"
          >
            <MenuCloseIcon className="size-6" />
          </IconButton>
          <DialogTitle className="sr-only">Image preview</DialogTitle>
          <DialogDescription className="sr-only">
            Full-size preview of the selected image.
          </DialogDescription>
          <img
            src={src}
            alt={alt}
            className="max-h-[90vh] w-auto max-w-full object-contain"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
